<template>
  <div>
    <section>
      <div class="container">
        <div class="text-center">
          <h2 class="font-size-medium font-weight-bold">Minha Carteira</h2>
        </div>
      </div>
    </section>

    <!-- TABS -->
    <section class="mt-5">
      <div class="container">
        <b-tabs
          content-class="mt-4 mt-lg-5"
          nav-class="default-tabs"
        >
          <!-- CARDS -->
          <b-tab title="Cartões" active>
            <div class="text-center col-md-8 mx-md-auto mb-4 font-size-small">
              <p>Cartões de crédito são necessários para fazer compras e assinaturas na Scriv, mas fique tranquilo! Ao cadastrar um cartão, todos os dados são criptografados e armazenados de maneira segura.</p>
            </div>
            <div
              class="text-center"
              v-if="cards.length == 0 && cardsLoader"
            >
              <Loader
                color="color2"
                size="big"
              />
            </div>
            <div v-else>
              <div
                class="text-center"
                v-if="cards.length == 0"
              >
                <p>Nenhum cartão foi cadastrado até o momento</p>
              </div>
              <div
                class="d-md-flex justify-content-lg-center flex-wrap"
                v-else
              >
                <div
                  class="col-md-6 col-lg-5 mb-4"
                  v-for="card in cards"
                  :key="card.id"
                >
                  <CreditCardCard
                    :card="card"
                    :editable="true"
                    @credit-card-turned-primary="getCreditCards"
                    @credit-card-deleted="getCreditCards"
                  />
                </div>
              </div>
              <div class="mt-5 text-center">
                <Button
                  class="btn-outline-color2"
                  v-b-modal.create-credit-card
                >
                  Adicionar cartão
                </Button>
              </div>
              <CreditCardCreationModal
                @credit-card-created="getCreditCards"
              />
            </div>
          </b-tab> <!-- END CARDS -->

          <!-- BANK ACCOUNTS -->
          <b-tab title="Contas Bancárias">
            <div class="text-center col-md-8 mx-md-auto mb-4 font-size-small">
              <p>Contas bancárias apenas são necessárias caso você queira vender seu conteúdo na plataforma! Usaremos a conta cadastrada apenas para transferir os fundos quando você desejar fazer um saque e não faremos nenhum tipo de cobrança.</p>
            </div>
            <div
              class="text-center"
              v-if="bankAccounts.length == 0 && bankAccountsLoader"
            >
              <Loader
                color="color2"
                size="big"
              />
            </div>
            <div v-else>
              <div
                class="text-center"
                v-if="bankAccounts.length == 0"
              >
                <p>Nenhuma conta foi cadastrada até o momento</p>
              </div>
              <div
                class="d-md-flex justify-content-lg-center flex-wrap"
                v-else
              >
                <div
                  class="col-md-6 col-lg-5 mb-4"
                  v-for="account in bankAccounts"
                  :key="account.id"
                >
                  <BankAccountCard
                    :account="account"
                    :editable="true"
                    @bank-account-deleted="getBankAccounts"
                    @bank-account-turned-primary="getBankAccounts"
                  />
                </div>
              </div>
              <div class="mt-5 text-center">
                <Button
                  class="btn-outline-color2"
                  v-b-modal.create-bank-account
                >
                  Adicionar conta
                </Button>
                <BankAccountCreationModal
                  @bank-account-created="getBankAccounts"
                />
              </div>
            </div>
          </b-tab> <!-- END BANK ACCOUNTS -->

          <!-- WALLET -->
          <b-tab title="Extrato">
            <div
              class="text-center"
              v-if="!wallets && walletsLoader"
            >
              <Loader
                color="color2"
                size="big"
              />
            </div>
            <div v-else-if="wallets">
              <!-- AMOUNT SECTION -->
              <section class="mb-5">
                <div class="text-center d-flex flex-column flex-lg-row justify-content-center">
                  <div :class="{'background-white': !nightMode, 'background-black': nightMode}" class="d-inline-block shadow-light rounded-xl p-2 pl-4 align-self-center d-flex align-items-center min-height-wrap">
                    <div class="d-flex mx-auto justify-content-between align-items-center ">
                      <div class="d-flex justify-content-center flex-grow-1 mr-2 font-size-small mr-4">
                        <span class="text-color2">
                          <i class="fa fa-money text-color2 mr-1"></i> Saldo disponível:
                        </span>
                        <span class="ml-2">{{ wallets.amount_available_for_payment/100 | formatMoney }}</span>
                      </div>
                      <div class="d-flex justify-content-center flex-grow-1 font-size-small mr-4">
                        <span class="text-color2">
                          <i class="fa fa-money text-color2 mr-1"></i> Saldo a receber:
                        </span>
                        <span class="ml-2">{{ wallets.amount_frozen/100 | formatMoney }}</span>
                      </div>
                      <div class="d-flex justify-content-center flex-grow-1">
                        <Button
                          class="btn btn-outline-color2"
                          :disabled="wallets.amount_available_for_payment <= 0 && 1 == 2"
                          v-b-modal.create-wallet-checkout
                        >
                          Realizar saque
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <!-- END AMOUNT SECTION -->

              <!-- Checkout modal -->
              <WalletCheckoutCreationModal
                @wallet-checkout-created="getWallets"
              />

              <!-- FILTER -->
              <section class="mb-4">
                <div class="d-flex text-center align-items-center justify-content-center font-size-small">
                  Filtrar de
                  <DateTimeInput
                    v-model.trim="walletStart"
                    format="dd/MM/yyyy"
                    type="date"
                    theme="basic"
                    class="mx-2"
                  />
                  até
                  <DateTimeInput
                    v-model.trim="walletEnd"
                    format="dd/MM/yyyy"
                    type="date"
                    theme="basic"
                    class="mx-2"
                  />
                </div>
              </section> <!-- END FILTER -->

              <section class="mb-4">
                <div class="container text-center">
                  <div class="col-lg-6 mx-auto">
                    <p class="font-size-xSmall">
                      Essa funcionalidade ainda está em fase beta de funcionamento. Em caso de problemas, entre em contato conosco nas nossas redes sociais ou em time@scriv.com.br.
                    </p>
                  </div>
                </div>
              </section>

              <!-- WALLET DATA -->
              <section>
                <div v-if="wallets.entity.length > 0">
                  <div
                    class="mb-3 mb-lg-4"
                    v-for="(wallet, index) in wallets.entity"
                    :key="index"
                  >
                    <WalletDayCard
                      :wallet="wallet"
                      :expandable="true"
                      :date="wallet.day"
                    />
                  </div>
                </div>
                <div v-else>
                  <p class="text-center">Não foi encontrado nenhum lançamento no período</p>
                </div>
              </section> <!-- END WALLET DATA -->
            </div>
          </b-tab> <!-- END WALLET -->

        </b-tabs>
      </div>
    </section> <!-- END TABS -->
  </div>
</template>

<script>

import BankAccountsService from '@/modules/bank-accounts/services/bank-accounts-service'
import CreditCardsService from '@/modules/credit-cards/services/credit-cards-service'
import WalletsService from '@/modules/wallets/services/wallets-service'
import moment from 'moment'
import { mapState } from 'vuex'

import BankAccountCard from '@/components/bank-accounts/BankAccountCard'
import BankAccountCreationModal from '@/components/bank-accounts/BankAccountCreationModal'
import Button from '@/components/common/Button/Button'
import CreditCardCard from '@/components/credit-cards/CreditCardCard'
import CreditCardCreationModal from '@/components/credit-cards/CreditCardCreationModal'
import DateTimeInput from '@/components/common/Input/DateTimeInput'
import Loader from '@/components/common/Loader/Loader'
import WalletDayCard from '@/components/wallets/WalletDayCard'
import WalletCheckoutCreationModal from '@/components/wallets/WalletCheckoutCreationModal'

export default {
  components: {
    BankAccountCard,
    BankAccountCreationModal,
    Button,
    CreditCardCard,
    CreditCardCreationModal,
    DateTimeInput,
    Loader,
    WalletDayCard,
    WalletCheckoutCreationModal
  },
  metaInfo () {
    return {
      title: 'Minha Carteira',
      meta: [
        { vmid: 'og-title', name: 'og:title', content: 'Minha Carteira' }
      ]
    }
  },
  data () {
    return {
      cards: [],
      wallets: null,
      bankAccounts: [],
      cardsLoader: false,
      walletsLoader: false,
      bankAccountsLoader: false,
      walletStart: moment().subtract(60, 'days').format(),
      walletEnd: moment().add(60, 'days').format()
    }
  },
  created () {
    this.getCreditCards()
    this.getBankAccounts()
    this.getWallets()
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    nightMode () {
      return localStorage.getItem('nightMode') === 'true'
    }
  },
  watch: {
    walletStart (newValue, oldValue) {
      this.getWallets()
    },
    walletEnd (newValue, oldValue) {
      this.getWallets()
    }
  },
  methods: {
    async getCreditCards () {
      this.cardsLoader = true
      this.cards = []
      try {
        const cards = await CreditCardsService.getCreditCards()
        this.cards = [...this.cards, ...cards]
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.cardsLoader = false
    },
    async getBankAccounts () {
      this.bankAccounts = []
      this.bankAccountsLoader = true
      try {
        const bankAccounts = await BankAccountsService.getBankAccounts()
        this.bankAccounts = [...this.bankAccounts, ...bankAccounts]
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.bankAccountsLoader = false
    },
    async getWallets () {
      this.wallets = null
      this.walletsLoader = true
      try {
        const wallets = await WalletsService.getGroupedWalletsInfoByDay({ start: moment(this.walletStart).format('YYYY-MM-DD'), end: moment(this.walletEnd).format('YYYY-MM-DD') })
        this.wallets = wallets
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.walletsLoader = false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .vdatetime-input {
    color: $color2;
  }
}
</style>
